import './MatchCard.css'

import type { Match } from '../store'

import { usePhotoObjectUrl } from '../hooks/usePhotoObjectUrl'
import { cx } from '../utilities/cx'
import { GallerySpacer } from './ImageGallery'
import { Tooltip } from './Tooltip'

export type MatchCardProps = {
  className?: string
  match: Match
  isSelected?: boolean
  isNewMatch?: boolean
  onClick?: () => void
}

function calculateAge(birthday: string | undefined): number | null {
  if (!birthday) return null
  const birthDate = new Date(birthday)
  const today = new Date()
  let age = today.getFullYear() - birthDate.getFullYear()
  const monthDiff = today.getMonth() - birthDate.getMonth()
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age -= 1
  }
  return age
}

function formatPercent(score: number): string {
  return Math.round(score * 100) + '%'
}

export const MatchCard = (props: MatchCardProps) => {
  const { className, match, isSelected, isNewMatch, onClick } = props
  const { profile } = match

  const age = calculateAge(profile.birthday)
  const photoId = profile.mainPhotoId ?? profile.approvedPhotoIds[0]
  const mainPhoto = usePhotoObjectUrl(photoId, 400)

  const classNames = cx(
    'match-card',
    { 'match-card--selected': isSelected, 'match-card--new': isNewMatch },
    className,
  )

  return (
    <div className={classNames} onClick={onClick}>
      {isNewMatch && (
        <>
          <div className="match-card__shine" />
          <div className="match-card__sparks">
            <div className="match-card__spark" />
            <div className="match-card__spark" />
            <div className="match-card__spark" />
            <div className="match-card__spark" />
            <div className="match-card__spark" />
            <div className="match-card__spark" />
            <div className="match-card__spark" />
            <div className="match-card__spark" />
            <div className="match-card__spark" />
            <div className="match-card__spark" />
            <div className="match-card__spark" />
            <div className="match-card__spark" />
          </div>
        </>
      )}
      {isNewMatch && <div className="match-card__glow" />}
      <div className="match-card__photo-container">
        {mainPhoto ? (
          <img className="match-card__photo" src={mainPhoto} alt={profile.name} />
        ) : (
          <GallerySpacer className="match-card__photo-placeholder" />
        )}
        <div className="match-card__match-badge">{formatPercent(match.score)} Match</div>
        {match.isSimulated && <div className="match-card__sim-badge">Simulated</div>}
        <div className="match-card__tap-hint">Tap to view</div>
      </div>

      <div className="match-card__content">
        <div className="match-card__header">
          <div className="match-card__name">
            {profile.name}
            {age !== null && <span className="match-card__age">, {age}</span>}
          </div>
          {profile.location && <div className="match-card__location">{profile.location}</div>}
        </div>

        <div className="match-card__compatibility">
          <div className="match-card__compatibility-header">
            <span className="match-card__compatibility-value">
              {formatPercent(match.theirScore)}
            </span>
            <span className="match-card__compatibility-label">Compatibility</span>
            <span className="match-card__compatibility-value">
              {formatPercent(match.yourScore)}
            </span>
          </div>
          <div className="match-card__compatibility-bar">
            <div
              className="match-card__compatibility-fill match-card__compatibility-fill--theirs"
              style={{ width: formatPercent(match.theirScore) }}
            />
            <div className="match-card__compatibility-center" />
            <div
              className="match-card__compatibility-fill match-card__compatibility-fill--yours"
              style={{ width: formatPercent(match.yourScore) }}
            />
          </div>
          <Tooltip
            className="match-card__compatibility-overlay match-card__compatibility-overlay--left"
            content="How well they fit you"
            position="top"
          >
            <div className="match-card__compatibility-overlay-target" />
          </Tooltip>
          <Tooltip
            className="match-card__compatibility-overlay match-card__compatibility-overlay--right"
            content="How well you fit them"
            position="top"
          >
            <div className="match-card__compatibility-overlay-target" />
          </Tooltip>
        </div>
      </div>
    </div>
  )
}
